// App shell — keeps the initial bundle slim.
//
// This module is the entry into <App />. It contains NO static imports of
// Firebase, charts, or the authenticated-app dependency graph. Marketing
// visitors (Landing, Pricing, Contact, etc.) hit this file and never download
// the 388 KB Firebase SDK or the 412 KB recharts bundle.
//
// Routing fork:
//   - Public marketing routes → lazy MarketingApp (Firebase-free chunk).
//   - Everything else        → lazy AppAuthed (full app, Firebase + Sentry).
//
// Both branches are React.lazy so each lands in its own JS chunk. The
// marketing chunk reuses vendor-react but skips vendor-firebase entirely.

import { lazy, Suspense, useSyncExternalStore } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { CookieBanner } from "./components/CookieBanner";
import { PageLoader } from "./components/PageLoader";
import { isAuthProcessed, onAuthProcessed } from "./lib/authFlag";

const MarketingApp = lazy(() => import("./MarketingApp").then(m => ({ default: m.MarketingApp })));
const AppAuthed = lazy(() => import("./AppAuthed"));
// Sonner's <Toaster> static-imports react-dom (default export) which rolldown
// then folds into vendor-charts. The result is that the marketing entry chunk
// statically reach-pulls vendor-charts (~250 KB) on every landing page load
// just to satisfy that one import. Lazy-loading the Toaster keeps sonner +
// its react-dom dependency out of the boot graph; toast.success() in user
// code still works because the API is a singleton store, not bound to the
// Toaster component itself.
const Toaster = lazy(() => import("sonner").then(m => ({ default: m.Toaster })));

// Routes that the marketing-only chunk handles. Anything else falls through
// to the authenticated app. Keep this list in sync with MarketingApp.tsx.
//
// We match on `location.pathname` (not React Router yet) because we want the
// fork to happen BEFORE the heavy AppAuthed module is even evaluated. React
// Router is imported either way because main.tsx wraps us in <BrowserRouter>.
const MARKETING_ROUTES = new Set([
  "/",
  "/pricing",
  "/get-iq-score",
  "/founding-agent",
  "/contact",
  "/privacy",
  "/terms",
  "/brokerage",
  "/for-brokerages",
  "/faq",
  "/how-it-works",
  "/features",
  "/compare",
  "/blog",
  "/500",
  "/founding-agent",
  "/get-iq-score",
  "/score",
]);

function isMarketingPath(pathname: string): boolean {
  if (MARKETING_ROUTES.has(pathname)) return true;
  // Public IQ Score lead magnet — /score, /score/badge (embeddable iframe),
  // and /score/:city SEO leaderboards. All unauthenticated + Firebase-free.
  if (pathname === '/score' || pathname.startsWith('/score/')) return true;
  // Public sample IQ reports (/samples/iq-reports/:id) are crawler-facing and
  // unauthenticated — keep them in the Firebase-free marketing chunk.
  if (pathname.startsWith('/samples/iq-reports/')) return true;
  // Data room share-link URLs are public + token-gated; recipients are not
  // signed in, so they need to stay in the marketing chunk.
  if (pathname.startsWith('/data-room/')) return true;
  // Transaction status share-links are public; no sign-in required.
  if (pathname.startsWith('/transaction/')) return true;
  // Public blog posts (/blog/:slug) are crawler-facing and unauthenticated.
  if (pathname.startsWith('/blog/')) return true;
  // Public, shareable sample IQ reports (/samples/iq-reports/:id) — no auth.
  if (pathname.startsWith('/samples/iq-reports/')) return true;
  return false;
}


function useAuthProcessed(): boolean {
  return useSyncExternalStore(onAuthProcessed, isAuthProcessed);
}

export default function App() {
  const location = useLocation();
  const justAuthed = useAuthProcessed();
  const marketing = isMarketingPath(location.pathname);

  // If the user just completed a Google redirect while on a marketing URL,
  // jump straight to the authenticated app shell. This avoids a full reload
  // and the associated landing-page flicker.
  if (marketing && justAuthed) {
    return (
      <>
        <Suspense fallback={null}>
          <Toaster position="top-right" richColors containerAriaLabel="Notifications" />
        </Suspense>
        <Suspense fallback={<PageLoader />}>
          <AppAuthed />
        </Suspense>
      </>
    );
  }

  if (marketing) {
    return (
      <ErrorBoundary>
        <Suspense fallback={null}>
          <Toaster position="top-right" richColors containerAriaLabel="Notifications" />
        </Suspense>
        <Suspense fallback={<PageLoader />}>
          <Routes location={location}>
            <Route path="*" element={<MarketingApp />} />
          </Routes>
        </Suspense>
        <CookieBanner />
      </ErrorBoundary>
    );
  }

  return (
    <>
      <Suspense fallback={null}>
        <Toaster position="top-right" richColors containerAriaLabel="Notifications" />
      </Suspense>
      <Suspense fallback={<PageLoader />}>
        <AppAuthed />
      </Suspense>
    </>
  );
}
